'use client';

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/app/providers';
import { favoriteService } from '@/services/favoriteService';
import { useToast } from '@/components/ui/Toast';
import { t } from '@/locales';

export function useFavorites() {
  const { user } = useAuth();
  const toast = useToast();
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setFavoriteIds(new Set());
      return;
    }
    setLoading(true);
    favoriteService.getFavoriteIds()
      .then(ids => setFavoriteIds(new Set(ids)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.id]);

  const isFavorite = useCallback((boatId: string) => favoriteIds.has(boatId), [favoriteIds]);

  const toggleFavorite = useCallback(async (boatId: string) => {
    if (!user?.id) return false;
    try {
      const favorited = await favoriteService.toggleFavorite(boatId);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (favorited) next.add(boatId);
        else next.delete(boatId);
        return next;
      });
      toast.success(favorited ? t('favorites.added') : t('favorites.removed'));
      return favorited;
    } catch {
      return false;
    }
  }, [user?.id, toast]);

  return { favoriteIds, isFavorite, toggleFavorite, loading };
}
